import type { PushSdkConfig } from '../../types/push-sdk-config.ts'
import type { DomainConfig } from '@core/types/domain-config.ts'
import { Env } from '../../env.ts'
import { arrayBufferFromBase64 } from '@core/utils/safe-base64.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { Globals } from '../../globals.ts'
import { ensureError } from '@core/utils/ensure-error.ts'
import { timeLimitedRunner } from '@core/timer/time-limited-runner.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { Result } from '@core/models/result.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { StaticEventDispatcher } from '@core/static-event-dispatcher.ts'
import { delay } from '@core/utils/delay.ts'

export type WebWorkerService = {
    /**
     * Attempts to wait, up to passed `maxWaitMilliseconds` value, for the browser service worker to signal
     * the registration is ready.
     */
    _ready(maxWaitMilliseconds: number): Promise<ServiceWorkerRegistration | void>
    /**
     * Attempts to wait, up to 50ms, for the browser service worker to signal
     * the registration is ready.
     */
    _ready(): Promise<ServiceWorkerRegistration | void>

    /**
     * Attempts to locate and return the PushlySW worker
     * via a combination of scope and path.
     *
     * Depending on domain flags 3rd-party workers could
     * be removed during the location loop.
     */
    _getRegistration(): Promise<ServiceWorkerRegistration | null>

    /**
     * Returns the computed values for worker installation path
     * and the worker {@link RegistrationOptions}.
     */
    _getRegistrationOptions(): [path: string, options: RegistrationOptions]

    /**
     * Attempts to create a new {@link ServiceWorkerRegistration}
     * or returns the existing one.
     *
     * @throws PushWorkerServiceException.registrationError
     */
    _register(existing?: ServiceWorkerRegistration | null): Promise<ServiceWorkerRegistration>

    /**
     * Returns the current {@link PushSubscription} if one exists.
     *
     * Pass an already-resolved registration to avoid re-running the (side-effecting)
     * registration scan when the caller has one in hand.
     */
    _getSubscription(registration?: ServiceWorkerRegistration | null): Promise<PushSubscription | null>

    /**
     * Attempts to create a new {@link PushSubscription} or returns
     * the existing one.
     */
    _subscribe(): Promise<Result<PushSubscription | null, Error | PNException>>

    /**
     * If a current subscription exists {@link PushSubscription.unsubscribe}
     * will be invoked.
     *
     * Returns a boolean indicating if the unsubscribe process was invoked
     * and successful.
     *
     * When `subscription` is given it is used as is, so a caller that already resolved
     * a still-activating worker's subscription does not lose it to a second, cold read.
     */
    _unsubscribe(subscription?: PushSubscription | null): Promise<boolean>

    /**
     * Resolves the current subscription, waiting for a still-activating worker to surface it.
     * Returns `null` when no registration exists.
     */
    _resolveColdSubscription(): Promise<PushSubscription | null>

    /**
     * Closes every notification this origin's worker is displaying. The worker reports clicks and
     * closes without consulting the SDK's halt state, so one left on screen keeps reporting.
     */
    _closeDisplayedNotifications(): Promise<void>

    /**
     * Returns true when an active subscription exists but was minted under a different
     * VAPID applicationServerKey than the domain's current key (publisher key rotation).
     * Such an endpoint silently fails delivery; the SDK should re-subscribe.
     */
    _hasStaleVapidSubscription(): Promise<boolean>
}

/** @internal */
export const PushWorkerServiceException = {
    registrationError: (err?: unknown, isSuppressed: boolean = false): PNException => {
        const error = err ? ensureError(err) : undefined
        return PNException._from(error, null, PNExceptionCode.PUSH_WORKER_REGISTRATION_EXCEPTION, isSuppressed)
    },
    workerNotFoundError: (err?: unknown): PNException => {
        const error = err ? ensureError(err) : undefined
        return PNException._from(error, null, PNExceptionCode.PUSH_WORKER_NOT_FOUND_EXCEPTION)
    },
    subscriptionError: (err?: unknown, isSuppressed: boolean = false): PNException => {
        const error = err ? ensureError(err) : undefined
        return PNException._from(error, null, PNExceptionCode.PUSH_WORKER_SUBSCRIPTION_EXCEPTION, isSuppressed)
    },
} as const

/** @internal */
export class PushWorkerService implements WebWorkerService {
    private readonly logger: Logger

    /**
     * Single-flight guards. Resolving a registration and subscribing both mutate durable
     * device state (unregistering a worker / minting an endpoint), and these run from several
     * entry points — init sync, a page-change scan, a user prompt. Without guarding, a scan
     * racing an in-flight subscribe (or two concurrent subscribes) could unregister a worker
     * out from under a subscribe, orphan a live endpoint, or mint a duplicate. Concurrent
     * callers share the in-flight promise instead of racing, and the destructive duplicate
     * cleanup is deferred while a subscribe is in flight.
     */
    private _getRegistrationInFlight: Promise<ServiceWorkerRegistration | null> | null = null
    private _subscribeInFlight: Promise<Result<PushSubscription | null>> | null = null

    public constructor(
        private readonly pushSdkConfig: PushSdkConfig,
        private readonly domain: DomainConfig,
        logger?: Logger,
    ) {
        this.logger = logger ?? TaggedLogger._getInstance('PushWorkerService')
    }

    public get _worker(): ServiceWorkerContainer {
        return Env._navigator.serviceWorker
    }

    public get _location(): Location {
        return Env._location
    }

    public async _ready(maxWaitMilliseconds?: number): Promise<ServiceWorkerRegistration | void> {
        return timeLimitedRunner(maxWaitMilliseconds ?? 50, this._worker.ready)
    }

    public async _getRegistration(): Promise<ServiceWorkerRegistration | null> {
        // Single-flight: collapse concurrent scans (e.g. a page-change scan overlapping a
        // subscribe's scan) so the destructive duplicate cleanup can't run twice and pick
        // different keepers — and so the scan isn't redundantly repeated within one call.
        if (this._getRegistrationInFlight) {
            return this._getRegistrationInFlight
        }
        this._getRegistrationInFlight = this._resolveRegistration()
        try {
            return await this._getRegistrationInFlight
        } finally {
            this._getRegistrationInFlight = null
        }
    }

    protected async _resolveRegistration(): Promise<ServiceWorkerRegistration | null> {
        try {
            const registrations = await this._worker.getRegistrations()
            if (!registrations || !registrations.length) {
                return null
            }

            const [pushlyPath, pushlyOptions] = this._getRegistrationOptions()
            const pushlyPathName = pushlyPath.split('?')[0]

            const withoutTrailingSlash = (str: string) => str.replace(/\/+$/, '')
            const pushlyScope = pushlyOptions.scope ? withoutTrailingSlash(pushlyOptions.scope) : undefined

            /**
             * When TRUE denotes we should unregister non-pushly
             * workers in same path but different scopes.
             */
            const shouldUnregisterNonPushlyWorkers = this.domain.flags.includes(
                Globals._swShouldUnregisterNonPushlyWorkersFlag,
            )

            const pushlyRegistrations: ServiceWorkerRegistration[] = []

            for (let registration of registrations) {
                /**
                 * Scope and Path matches cannot be reliably evaluated
                 * until the service worker is in a ready state. Each
                 * iteration will wait for a max of 50ms before continuing
                 * as we also do not wish to hang the user experience.
                 */
                await this._ready(50)

                const workerPath = this._resolveRegistrationScriptUrl(registration)
                const isScopeMatch = withoutTrailingSlash(registration.scope) === pushlyScope

                /**
                 * A registration running the pushly worker script is *ours*,
                 * regardless of its scope. Matching on the script path (rather than
                 * requiring an exact scope match) lets us recognize: (a) a worker a
                 * migrating publisher registered at a non-normalized scope, and (b) a
                 * worker that has not yet activated (cold `active`, resolved here via
                 * the waiting/installing worker). Both previously looked "foreign",
                 * which caused a second pushly worker to be registered alongside the
                 * live one — leaving two pushly workers registered for one origin (NS-2554).
                 */
                const isPushlyWorker = !!workerPath && workerPath.includes(pushlyPathName)

                if (isPushlyWorker) {
                    /**
                     * Collect every pushly worker; the keeper is chosen after the scan.
                     * Multiple pushly workers at different scopes are duplicates (from
                     * trailing-slash/host scope normalization or the cold-worker race that
                     * spawned a second registration). Two registrations for the same origin
                     * each display the push and each emit impression/click events, so they
                     * double-count events and destabilize delivery — collapse to one (NS-2554).
                     */
                    pushlyRegistrations.push(registration)
                    continue
                }

                // non-pushly registration found in external scope
                // if unregister flag exists remove registration
                if (!isScopeMatch && shouldUnregisterNonPushlyWorkers) {
                    await registration.unregister()
                    this.logger.info(`Removed worker ${workerPath} at scope ${registration.scope}`)
                    continue
                }

                // non-pushly registration found in pushly defined scope
                // alert client if domain does not contain the proper flag
                if (
                    isScopeMatch &&
                    !this.domain.flags.includes(Globals._swShouldReplaceExistingWorkerInPushlyScopeFlag)
                ) {
                    this.logger.warn(
                        `Implementation Error. An existing, non-push, service worker was found at scope: ${pushlyOptions.scope}.`,
                    )
                }
            }

            /**
             * Sort by scope so keeper selection is deterministic regardless of the
             * unspecified `getRegistrations()` ordering. Otherwise two scans could pick
             * different keepers and unregister each other's, leaving the origin flipping
             * between registrations across loads.
             */
            pushlyRegistrations.sort((a, b) => a.scope.localeCompare(b.scope))

            /**
             * Pick the keeper *preferring the registration that owns a live push
             * subscription*, falling back to the first (now deterministically ordered).
             * Keeping array order blindly could unregister the duplicate that actually holds
             * the active endpoint — orphaning it and forcing a needless re-subscribe.
             */
            const reg = await this._selectKeeperRegistration(pushlyRegistrations)

            /**
             * Remove any duplicate pushly workers, keeping the one resolved above.
             * This guarantees a single pushly registration per origin going forward
             * even for installs that already accumulated duplicates in the field.
             *
             * Gated by a default-on kill switch: the destructive `unregister()` is the one
             * irreversible step here (a mis-selected keeper would orphan a live endpoint),
             * so {@link Globals._swDisableDuplicateWorkerCleanupFlag} lets us disable it for a
             * domain via config — instantly, no SDK re-release — if it ever misbehaves.
             *
             * Skip the destructive unregister while a subscribe is in flight: that subscribe
             * may be operating on one of these registrations. Cleanup runs on the next scan
             * (page change / sync) when nothing is mid-subscribe.
             */
            const duplicateCleanupDisabled = this.domain.flags.includes(
                Globals._swDisableDuplicateWorkerCleanupFlag,
            )
            const subscribeInFlight = this._subscribeInFlight !== null
            for (let duplicate of pushlyRegistrations) {
                if (duplicate === reg) {
                    continue
                }
                if (duplicateCleanupDisabled || subscribeInFlight) {
                    this.logger.info(
                        `Duplicate pushly worker at scope ${duplicate.scope} left in place ` +
                            `(${duplicateCleanupDisabled ? 'cleanup disabled' : 'subscribe in flight'})`,
                    )
                    continue
                }
                await duplicate.unregister()
                this.logger.info(`Removed duplicate pushly worker at scope ${duplicate.scope}`)
            }

            return reg
        } catch (err) {
            const error = ensureError(err)
            StaticEventDispatcher._dispatch(StaticEventDispatcher.EventType.ERROR, error)
            this.logger.error(error)
        }

        return null
    }

    /**
     * Resolves the script URL for a registration, preferring the active worker but
     * falling back to the waiting/installing worker so a not-yet-activated (cold)
     * registration is still identifiable. Reading only `active.scriptURL` returned
     * `''` for a cold worker, which made an existing pushly worker look foreign and
     * triggered a duplicate registration (NS-2554).
     */
    protected _resolveRegistrationScriptUrl(registration: ServiceWorkerRegistration): string {
        return (
            registration.active?.scriptURL ||
            registration.waiting?.scriptURL ||
            registration.installing?.scriptURL ||
            ''
        )
    }

    /**
     * Chooses which pushly registration to keep when duplicates exist, preferring one
     * that currently owns a {@link PushSubscription} so collapsing duplicates never orphans
     * the live endpoint. Falls back to the first registration when none expose a
     * subscription (or the lookup throws).
     */
    protected async _selectKeeperRegistration(
        registrations: ServiceWorkerRegistration[],
    ): Promise<ServiceWorkerRegistration | null> {
        if (!registrations.length) {
            return null
        }

        for (let registration of registrations) {
            try {
                if (await registration.pushManager.getSubscription()) {
                    return registration
                }
            } catch {
                // Ignore — a registration we cannot probe simply isn't preferred.
            }
        }

        return registrations[0]
    }

    /**
     * @throws PushWorkerServiceException.workerNotFoundError
     * @throws Error
     */
    public async _register(existing?: ServiceWorkerRegistration | null): Promise<ServiceWorkerRegistration> {
        // When the caller has already run the (side-effecting) registration scan, reuse
        // its result instead of scanning again. `undefined` means "not scanned yet".
        let reg = existing === undefined ? await this._getRegistration() : existing
        if (reg) {
            return reg
        }

        const [path, options] = this._getRegistrationOptions()
        this.logger.info(`Installing new registration: ${path}`)
        try {
            reg = await this._worker.register(path, options)
        } catch (err) {
            throw await this._sanitizeRegistrationError(ensureError(err), path)
        }

        /**
         * Waits for the found/new registration to respond with an active state.
         */
        const waitForReadyState = async (): Promise<void> => {
            const dts = new Date()

            const _waitForReadyState = (): Promise<void> =>
                new Promise((res) => {
                    if (reg?.active) {
                        this.logger.verbose(
                            'New registration is ready',
                            `${(new Date().getTime() - dts.getTime()) / 1000}s`,
                        )
                        return res()
                    }

                    setTimeout(_waitForReadyState, 10)
                })

            return _waitForReadyState()
        }

        await timeLimitedRunner(500, waitForReadyState)

        return reg
    }

    public async _getSubscription(
        registration?: ServiceWorkerRegistration | null,
    ): Promise<PushSubscription | null> {
        const reg = registration === undefined ? await this._getRegistration() : registration
        if (!reg) {
            return null
        }

        try {
            // Must `await` here — a bare `return promise` escapes this try/catch, so a
            // rejected getSubscription() would bypass the error dispatch and the Result
            // contract callers rely on.
            return await reg.pushManager.getSubscription()
        } catch (err) {
            const error = ensureError(err)
            StaticEventDispatcher._dispatch(StaticEventDispatcher.EventType.ERROR, error)
            this.logger.error(error)
        }

        return null
    }

    /**
     * Resolves an existing subscription that a cold (still-activating) worker may not have
     * surfaced yet, waiting up to the cold-subscription budget on `serviceWorker.ready`
     * before reading once more. Returns `null` when no registration exists — a first-time
     * subscriber has nothing to wait for (and `ready` would never resolve), so callers mint
     * promptly. The wait is event-driven: it resolves the instant the worker activates, so a
     * warm worker adds no latency; the budget only bounds the genuinely-cold case.
     */
    public async _resolveColdSubscription(): Promise<PushSubscription | null> {
        const reg = await this._getRegistration()
        if (!reg) {
            return null
        }

        await this._ready(Globals._coldSubscriptionResolutionBudgetMs)
        return this._getSubscription(reg)
    }

    /**
     * Returns true when an active subscription exists whose VAPID applicationServerKey
     * differs from the domain's current key — i.e. the publisher rotated their key. The
     * browser does not surface a publisher-side rotation (no `pushsubscriptionchange`),
     * so the stale endpoint would otherwise persist and silently fail delivery (NS-2557).
     */
    public async _hasStaleVapidSubscription(): Promise<boolean> {
        const sub = await this._getSubscription()
        if (!sub) {
            return false
        }
        return !this._subscriptionUsesCurrentVapidKey(sub)
    }

    /**
     * Compares the subscription's applicationServerKey to the domain's current VAPID key.
     * When the browser does not expose the existing key (or it is not a comparable
     * buffer) we cannot conclude a mismatch, so we treat it as a match to avoid churning
     * a subscription we cannot evaluate.
     */
    protected _subscriptionUsesCurrentVapidKey(subscription: PushSubscription): boolean {
        const existingKey = subscription.options?.applicationServerKey
        if (!(existingKey instanceof ArrayBuffer)) {
            return true
        }

        try {
            return this._areApplicationServerKeysEqual(
                existingKey,
                arrayBufferFromBase64(this.domain.vapid_public_key),
            )
        } catch {
            return true
        }
    }

    private _areApplicationServerKeysEqual(a: ArrayBuffer, b: ArrayBuffer | Uint8Array): boolean {
        const aBytes = new Uint8Array(a)
        const bBytes = b instanceof Uint8Array ? b : new Uint8Array(b)

        if (aBytes.byteLength !== bBytes.byteLength) {
            return false
        }

        for (let i = 0; i < aBytes.byteLength; i++) {
            if (aBytes[i] !== bBytes[i]) {
                return false
            }
        }

        return true
    }

    // We see a few instances where the response of `registration.pushManager.subscribe()`
    // returns null instead of a PushSubscription and does not throw so to safeguard
    // downstream consumers the return value is `PushSubscription | null`

    public async _subscribe(): Promise<Result<PushSubscription | null>> {
        // Single-flight: concurrent subscribe attempts (auto-sync racing a user prompt, or a
        // double-invoke) share one in-flight operation instead of each minting an endpoint.
        if (this._subscribeInFlight) {
            return this._subscribeInFlight
        }
        this._subscribeInFlight = this._performSubscribe()
        try {
            return await this._subscribeInFlight
        } finally {
            this._subscribeInFlight = null
        }
    }

    protected async _performSubscribe(): Promise<Result<PushSubscription | null>> {
        let curr = await this._getSubscription()
        if (!curr) {
            // The read above goes through the brief `_ready(50)` window, which a cold
            // (still-activating) worker frequently misses — so an existing live subscription
            // can read as absent. Minting now would orphan that endpoint and create a
            // duplicate. Give the worker the cold budget to surface its existing subscription
            // before concluding it's gone. Doing this here guards every caller of `_subscribe`
            // (the prompt-grant path and every silent-subscription branch), not only the one
            // caller (the granted+subscribed reconcile path) that pre-resolves separately (NS-2554).
            curr = await this._resolveColdSubscription()
        }
        if (curr) {
            if (this._subscriptionUsesCurrentVapidKey(curr)) {
                this.logger.debug('Existing subscription found')
                return Result.Ok(curr)
            }

            // The existing endpoint was minted under a now-rotated VAPID
            // applicationServerKey and will silently fail delivery. Tear it down so the
            // loop below re-subscribes under the current key (NS-2557).
            this.logger.info('Existing subscription uses a stale VAPID key; re-subscribing under the current key')
            try {
                await curr.unsubscribe()
            } catch (err) {
                this.logger.warn('Failed to remove stale-key subscription', ensureError(err).message)
            }
        }

        let reg = await this._getRegistration()
        if (!reg) {
            try {
                // Pass the (null) scan result so _register doesn't re-run the scan — the
                // registration resolution previously ran up to three times per subscribe.
                reg = await this._register(reg)
            } catch (err) {
                return Result.Err(PushWorkerServiceException.registrationError(err))
            }
        }

        this.logger.info('Subscribing')

        const maxRetries = 10
        const maxRetryDelayMs = 500
        const applicationServerKey = arrayBufferFromBase64(this.domain.vapid_public_key)
        let response: Result<PushSubscription | null> = Result.Ok(null)

        for (let currAttempt = 0; currAttempt++ < maxRetries; ) {
            try {
                await this._ready()

                const subscription = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey,
                })

                if (subscription) {
                    response = Result.Ok(subscription)
                    break
                }
            } catch (err) {
                const original = ensureError(err)
                const error = this._sanitizeSubscriptionError(original)

                // Terminal failures (permission denial, unusable key) cannot succeed on
                // retry — fail fast instead of burning the full retry budget (~2.5s) on
                // an outcome that will never change.
                if (currAttempt === maxRetries || !this._isRetryableSubscriptionError(original)) {
                    response = Result.Err(error)
                    break
                }

                this.logger.warn(error)
            }

            this.logger.debug('Retrying subscription')
            await delay(Math.floor(Math.random() * maxRetryDelayMs))
        }

        return response
    }

    /**
     * Whether a failed `pushManager.subscribe()` is worth retrying. Permission denial
     * and an unusable/unretrievable VAPID key are terminal for the lifetime of this
     * call; everything else (transient push-service/network/abort errors, a not-yet
     * active worker) may succeed on a subsequent attempt.
     */
    protected _isRetryableSubscriptionError(error: Error): boolean {
        if (error.name === 'NotAllowedError') {
            return false
        }

        return !/could not retrieve the public key/i.test(error.message)
    }

    public async _unsubscribe(subscription?: PushSubscription | null): Promise<boolean> {
        try {
            const sub = subscription === undefined ? await this._getSubscription() : subscription
            if (sub) {
                await sub.unsubscribe()
            }

            return true
        } catch (err) {
            const error = ensureError(err)
            StaticEventDispatcher._dispatch(StaticEventDispatcher.EventType.ERROR, error)
            this.logger.error(error.message)
        }

        return false
    }

    public async _closeDisplayedNotifications(): Promise<void> {
        try {
            const registration = await this._getRegistration()
            const notifications = (await registration?.getNotifications()) ?? []
            notifications.forEach((notification) => notification.close())
        } catch (err) {
            this.logger.warn(ensureError(err).message)
        }
    }

    public _getRegistrationOptions(): [path: string, options: RegistrationOptions] {
        const loc = this._location

        const swHost = `${loc.protocol}//${loc.hostname}${loc.port ? `:${loc.port}` : ''}`
        const swConfigValue = this.pushSdkConfig.sw
        const swScopeConfigValue = this.pushSdkConfig.swScope

        const sanitizeConfigValue = (value: string) => value.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/+/, '')

        let swConfigPathValue = !swConfigValue ? null : sanitizeConfigValue(swConfigValue)
        if (swConfigPathValue && !swConfigPathValue.includes(swHost)) {
            swConfigPathValue = `/${swConfigPathValue}`
        }

        let swConfigScopeValue = !swScopeConfigValue ? null : sanitizeConfigValue(swScopeConfigValue)
        if (swConfigScopeValue && !swConfigScopeValue.includes(swHost)) {
            swConfigScopeValue = `/${swConfigScopeValue}`
        }

        let swUrl = swConfigPathValue || '/pushly-sdk-worker.js'

        const swUrlScope = swUrl.substr(0, swUrl.lastIndexOf('/') + 1) || '/'
        const useRootScope = this.domain.flags.includes(Globals._swUseRootScopeFlag)

        let swScope = swUrlScope
        if (swConfigScopeValue) {
            swScope = swConfigScopeValue
        } else if (useRootScope) {
            swScope = '/'
        }

        let computedScope = swScope.includes(swHost) ? swScope : `${swHost.replace(/\/$/, '')}${swScope}`
        // ensure trailing slash
        computedScope = `${computedScope.replace(/\/$/, '')}/`

        // some clients strip URL parameters from the service worker, this is okay as long as we aren't serving a custom worker per domain
        const appendDomainKey = !this.domain.flags.includes(Globals._swUsePathWithoutDomainKeyFlag)
        if (appendDomainKey) {
            swUrl = `${swUrl}${swUrl.includes('?') ? '&' : '?'}domain_key=${this.pushSdkConfig.domainKey}`
        }

        return [
            swUrl,
            {
                updateViaCache: 'none',
                scope: computedScope,
            },
        ]
    }

    protected async _tryFetchResourceStatus(resource: string): Promise<number> {
        try {
            return (await fetch(resource)).status
        } catch {
            return 0
        }
    }

    protected async _sanitizeRegistrationError(error: Error, registrationPath: string): Promise<PNException | Error> {
        const knownMessagesPatterns: RegExp[] = [
            /SSL certificate/i,
            /Failed to access storage/i,
            /Operation has been aborted/i,
            /unknown error occurred when fetching the script/i,
            /403/,
        ]

        if (
            PNException.KNOWN_NAMES.includes(error.name) ||
            knownMessagesPatterns.some((pattern) => pattern.test(error.message))
        ) {
            return PushWorkerServiceException.registrationError(error, true)
        }

        const resourceStatus = await this._tryFetchResourceStatus(registrationPath)

        if (/404/.test(error.message) || resourceStatus === 404) {
            throw PushWorkerServiceException.workerNotFoundError(error)
        }

        // Unknown registration failure — include diagnostics (script URL, fetch status,
        // original error name) so the error event carries enough context to triage.
        // Without this, errors like "Rejected" give zero signal about the root cause.
        return new PNException(
            PNExceptionCode.PUSH_WORKER_REGISTRATION_EXCEPTION,
            error,
            `sw=${registrationPath} fetchStatus=${resourceStatus} errorName=${error.name}`,
        )
    }

    protected _sanitizeSubscriptionError(error: Error): PNException | Error {
        const knownMessagesPatterns: RegExp[] = [/push service error/i, /could not retrieve the public key/i]

        const isSuppressed =
            PNException.KNOWN_NAMES.includes(error.name) ||
            knownMessagesPatterns.some((pattern) => pattern.test(error.message)) ||
            // NotAllowedError = user denied push permission. This is an expected user action,
            // not an SDK error. Permission state is already tracked via the dedicated
            // PERMISSION_DENIED event, so the subscription error event is redundant telemetry.
            error.name === 'NotAllowedError'

        return PushWorkerServiceException.subscriptionError(error, isSuppressed)
    }
}
